import React from 'react';
import { Link } from 'react-router-dom';
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Users, Award, Target, Heart } from 'lucide-react';

const TEAM_IMAGE = "https://images.unsplash.com/photo-1521737711867-e3b97375f902";

const stats = [
  { value: '10+', label: 'Years Experience' },
  { value: '500+', label: 'Projects Delivered' },
  { value: '150+', label: 'Happy Clients' },
  { value: '25+', label: 'Team Members' }
];

const values = [
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'We focus on delivering measurable outcomes that drive real business growth.'
  },
  {
    icon: Heart,
    title: 'Client-First',
    description: 'Your success is our success. We build lasting partnerships, not just projects.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We hold ourselves to the highest standards in everything we do.'
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We work as an extension of your team, with transparent communication.'
  }
];

export default function AboutPage() {
  return (
    <Layout>
     <SEO
  title="Know About Us | Square Trigger"
  description="Know more about Square Trigger to get exceptional digital agency services in Vancouver and throughout Canada. Read more."
  keywords={SEO_KEYWORDS.about}
/>
     {/* Hero Section */}
<section className="pt-32 pb-20 relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
  <div className="absolute inset-0">
    <div className="absolute top-20 right-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Badge variant="secondary" className="mb-6 bg-primary/20 text-primary-foreground border-none">
            <Users className="h-4 w-4 mr-2" />
            About Us
          </Badge>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl font-display font-bold text-background mb-6"
        >
          Your Digital{" "}
          <span className="gradient-text">Success Partner</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg text-background/70 mb-8"
        >
          Square Trigger is a full-service digital agency helping Canadian businesses transform their digital presence. With over a decade of experience, we combine creativity with technology to deliver exceptional results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Button variant="gradient" size="lg" asChild className="group">
            <Link to="/get-started">
              Work With Us
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="relative w-full max-w-md lg:ml-auto"
      >
        <div className="rounded-2xl overflow-hidden">
          <img
            src={TEAM_IMAGE}
            alt="Square Trigger Team"
            className="w-full h-[420px] object-cover"
          />
        </div>
      </motion.div>
    </div>
  </div>
</section>

      {/* Stats */}
      <section className="py-16 bg-background border-b border-border">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-display font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
                Built on Passion,{' '}
                <span className="gradient-text">Driven by Results</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="prose prose-lg max-w-none text-muted-foreground"
            >
              <p>
                Square Trigger was founded with a simple mission: to help businesses succeed in the digital world. What started as a small team of passionate developers and designers has grown into a full-service digital agency serving clients across Canada and beyond.
              </p>
              <p>
                Over the years, we've had the privilege of working with startups, small businesses, and established enterprises, helping them build their digital presence, reach new customers, and achieve their business goals.
              </p>
              <p>
                Today, our team of experts brings together decades of combined experience in web development, SEO, digital marketing, and design. We're not just service providers—we're your partners in digital success.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What{' '}
              <span className="gradient-text">Drives Us</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6 bg-card rounded-xl border border-border"
                >
                  <div className="w-14 h-14 rounded-full gradient-primary-soft flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Let's discuss how we can help your business succeed.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Get Started
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
